import { useCallback } from 'react';

import {
  enrichResourceFileInfo,
  getContentTypeTag,
  getResourceContentErrorMessage,
  RESOURCE_FILE_UPLOAD_ERROR_CODE,
} from 'src/data/performance';
import { calculateFileMD5 } from 'src/lib/crypto';
import { useCustomerActions } from 'src/store/customer';
import { useResourceActions } from 'src/store/resource';

import { useCipEvents } from './useCipEvents';

export const useUploadResourceFile = () => {
  const customerActions = useCustomerActions();
  const resourceActions = useResourceActions();
  const cipEvents = useCipEvents();

  /**
   * Uploads a file as a resource and returns the resource object.
   * @param {Object} params - The parameters for the function.
   * @param {RESOURCE_CONTENT_TYPE} params.resourceType - The type of the resource.
   * @param {File} params.file - The file to be uploaded.
   */
  return useCallback(
    async ({ resourceType, file }) => {
      // Check if we already have this file uploaded as a "resource"
      const checksum = await calculateFileMD5(file);
      const existenceResult = await resourceActions.checkFileResourceExistence({
        type: resourceType,
        checksum,
      });

      // If we already have a resource (with "id") with the same file, we should try using it
      if (existenceResult?.exist) {
        // Check if the existing resource is associated with the current workspace
        const resource = resourceActions.getWorkspaceResource(existenceResult.id);
        if (!resource) {
          throw new Error(
            getResourceContentErrorMessage(
              resourceType,
              RESOURCE_FILE_UPLOAD_ERROR_CODE.fileExistsButNotAssociatedToWorkspace
            )
          );
        }

        return resource;
      }

      // If we don't have this file uploaded as a "resource", we should upload it
      const enrichedFile = await enrichResourceFileInfo({ resourceType, file });
      const extractedResource = await resourceActions.extractResource({
        name: enrichedFile.name,
        tags: [getContentTypeTag(resourceType)],
        file: enrichedFile,
        contentType: resourceType,
        checksum,
      });

      // Refresh the customer feature limit and track "add resource" event if an actual resource was extracted
      customerActions.refreshCurrentCustomerFeatureLimit();
      if (extractedResource) {
        cipEvents.trackAddResource();
      }

      return extractedResource;
    },
    [cipEvents, customerActions, resourceActions]
  );
};
